import { useLayoutEffect } from "react";

// Sub-pixel scroll offsets at either end still count as resting there.
const EDGE_TOLERANCE = 1;

const setAttribute = (element: HTMLElement, name: string, value: boolean) => {
    const text = String(value);
    if (element.getAttribute(name) !== text) {
        element.setAttribute(name, text);
    }
};

const setPixels = (element: HTMLElement, name: string, value: number) => {
    const text = `${value}px`;
    if (element.style.getPropertyValue(name) !== text) {
        element.style.setProperty(name, text);
    }
};

const writeEdges = (scroller: HTMLElement, target: HTMLElement) => {
    const {
        scrollTop,
        scrollLeft,
        scrollHeight,
        scrollWidth,
        clientHeight,
        clientWidth,
        offsetHeight,
        offsetWidth,
    } = scroller;
    setAttribute(target, "data-overflow-top", scrollTop > EDGE_TOLERANCE);
    setAttribute(
        target,
        "data-overflow-bottom",
        scrollHeight - clientHeight - scrollTop > EDGE_TOLERANCE,
    );
    setAttribute(target, "data-overflow-left", scrollLeft > EDGE_TOLERANCE);
    setAttribute(
        target,
        "data-overflow-right",
        scrollWidth - clientWidth - scrollLeft > EDGE_TOLERANCE,
    );
    setPixels(
        target,
        "--board-scroll-edges-scrollbar-width",
        offsetWidth - clientWidth,
    );
    setPixels(
        target,
        "--board-scroll-edges-scrollbar-height",
        offsetHeight - clientHeight,
    );
};

/**
 * Writes the edges once, then again at most once a frame on scroll and on
 * size changes of the scroller or its children. Returns the teardown.
 */
const watchScrollEdges = (scroller: HTMLElement, target: HTMLElement) => {
    let frame = 0;
    const update = () => {
        frame = 0;
        writeEdges(scroller, target);
    };
    const schedule = () => {
        if (frame === 0) {
            frame = requestAnimationFrame(update);
        }
    };
    const resizeObserver = new ResizeObserver(schedule);
    const mutationObserver = new MutationObserver((records) => {
        records.forEach(({ removedNodes, addedNodes }) => {
            removedNodes.forEach((node) => {
                if (node instanceof Element) {
                    resizeObserver.unobserve(node);
                }
            });
            addedNodes.forEach((node) => {
                if (node instanceof Element) {
                    resizeObserver.observe(node);
                }
            });
        });
        schedule();
    });
    update();
    resizeObserver.observe(scroller);
    Array.from(scroller.children).forEach((child) =>
        resizeObserver.observe(child),
    );
    mutationObserver.observe(scroller, { childList: true });
    scroller.addEventListener("scroll", schedule, { passive: true });
    return () => {
        cancelAnimationFrame(frame);
        resizeObserver.disconnect();
        mutationObserver.disconnect();
        scroller.removeEventListener("scroll", schedule);
    };
};

/**
 * Marks on `target` which edges of `scroller` have content beyond them
 * (`data-overflow-top|bottom|left|right="true"`) and the scroller's
 * scrollbar sizes (`--board-scroll-edges-scrollbar-width|height`), without
 * re-rendering.
 */
export const useScrollEdges = (
    scroller: HTMLElement | null,
    target: HTMLElement | null,
) => {
    useLayoutEffect(
        () =>
            scroller === null || target === null
                ? undefined
                : watchScrollEdges(scroller, target),
        [scroller, target],
    );
};
